/**
 * 튜닝 가이드의 **대안 우선순위** 편집(2026-10-11) — 대안이 여럿일 때 무엇을 먼저 검증하고 무엇에 값을 둘지.
 *
 *   처방 순서  처방 분류 코드를 순서대로. SQL 튜닝의 「AI 튜닝 자동 진행」·「순서대로 검증」 이 이 순서로 잰다
 *   가치 순서  한 줄에 하나. 최종안 순위의 근거로 AI 에 실린다
 *   건너뛰기   앞선 대안이 논리 읽기를 이만큼 줄이면 자동 진행은 뒤의 대안을 재지 않는다(비우면 끝까지 잰다)
 *
 * 가이드는 순서와 값만 정한다 — 측정과 판정(결과 값 대조·논리 읽기)은 그대로 기계가 한다.
 * 처방 코드 목록은 서버가 준다(분류 유형 관리와 같은 목록). 상한은 서버가 거부한다(자르면 뜻이 바뀐다) — 여기서는 넘기 전에 막아 보여 준다.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { FiArrowUp, FiArrowDown, FiX } from 'react-icons/fi';

export const emptyPriority = () => ({ fixOrder: [], values: '', stopAt: '' });
/** 서버 모양 → 편집 상태(가치 순서는 줄 글, 건너뛰기는 입력 칸의 글자) */
export const toPriorityForm = (p) => ({
    fixOrder: [...((p && p.fixOrder) || [])],
    values: ((p && p.values) || []).join('\n'),
    stopAt: p && p.stopAt && p.stopAt.buffersPct ? String(p.stopAt.buffersPct) : '',
});
/** 편집 상태 → 서버 모양. 전부 비었으면 null(우선순위를 정하지 않은 가이드) */
export const toPriorityPayload = (f) => {
    const values = String((f && f.values) || '').split('\n').map(x => x.trim()).filter(Boolean);
    const pct = Number(f && f.stopAt);
    const stopAt = Number.isFinite(pct) && pct > 0 ? { buffersPct: Math.round(pct) } : null;
    const fixOrder = (f && f.fixOrder) || [];
    return (!fixOrder.length && !values.length && !stopAt) ? null : { fixOrder, values, stopAt };
};
/** 넘기기 전에 보이는 문제 — { tooManyValues, longValue, badStop } */
export const priorityProblems = (f, limits) => {
    const values = String((f && f.values) || '').split('\n').map(x => x.trim()).filter(Boolean);
    const pct = String((f && f.stopAt) || '').trim();
    return {
        tooManyValues: values.length > limits.values,
        longValue: values.some(v => v.length > limits.valueChars),
        badStop: pct !== '' && !(Number(pct) >= 50 && Number(pct) <= 99),
    };
};

function TuningPriorityEditor({ value, onChange, fixTopics, readOnly, limits, t }) {
    const order = value.fixOrder;
    const labelOf = (code) => (fixTopics.find(x => x.code === code) || {}).label || code;
    const move = (i, d) => {
        const j = i + d;
        if (j < 0 || j >= order.length) return;
        const next = [...order];
        [next[i], next[j]] = [next[j], next[i]];
        onChange({ ...value, fixOrder: next });
    };
    const rest = fixTopics.filter(x => !order.includes(x.code));
    const problems = priorityProblems(value, limits);
    return (
        <div className="dg-priority">
            <div className="tcf-label">{t('assistant:tuningGuide.priority.title')}</div>
            <div className="tcf-hint">{t('assistant:tuningGuide.priority.hint')}</div>

            <div className="dg-priLabel">{t('assistant:tuningGuide.priority.fixOrder')}</div>
            {order.length ? (
                <ol className="dg-priList">
                    {order.map((code, i) => (
                        <li key={code} className="dg-priItem">
                            <span className="dg-priNo">{i + 1}</span>
                            <span className="dg-priName">{labelOf(code)}</span>
                            <code className="dg-priCode">{code}</code>
                            {!readOnly ? (
                                <span className="dg-priBtns">
                                    <button type="button" className="tcf-iconBtn" disabled={i === 0} onClick={() => move(i, -1)} title={t('assistant:tuningGuide.priority.up')} aria-label={t('assistant:tuningGuide.priority.up')}><FiArrowUp size={11} /></button>
                                    <button type="button" className="tcf-iconBtn" disabled={i === order.length - 1} onClick={() => move(i, 1)} title={t('assistant:tuningGuide.priority.down')} aria-label={t('assistant:tuningGuide.priority.down')}><FiArrowDown size={11} /></button>
                                    <button type="button" className="tcf-iconBtn" onClick={() => onChange({ ...value, fixOrder: order.filter(c => c !== code) })} title={t('common:ui.delete')} aria-label={t('common:ui.delete')}><FiX size={11} /></button>
                                </span>
                            ) : null}
                        </li>
                    ))}
                </ol>
            ) : <div className="tcf-hint">{t('assistant:tuningGuide.priority.noOrder')}</div>}
            {!readOnly && rest.length ? (
                <select
                    className="tcf-input ta-narrow" value="" aria-label={t('assistant:tuningGuide.priority.add')}
                    onChange={(e) => { if (e.target.value) onChange({ ...value, fixOrder: [...order, e.target.value] }); }}
                >
                    <option value="">{t('assistant:tuningGuide.priority.add')}</option>
                    {rest.map(x => <option key={x.code} value={x.code}>{x.label}</option>)}
                </select>
            ) : null}

            <div className="dg-priLabel">{t('assistant:tuningGuide.priority.values')}</div>
            <textarea
                className="tcf-input" rows={4} value={value.values} readOnly={readOnly}
                placeholder={t('assistant:tuningGuide.priority.valuesPh')}
                onChange={(e) => onChange({ ...value, values: e.target.value })}
            />
            {problems.tooManyValues || problems.longValue ? (
                <div className="gm-dropped">{t('assistant:tuningGuide.priority.valuesLimit', { count: limits.values, chars: limits.valueChars })}</div>
            ) : null}

            <div className="tcf-row dg-priStop">
                <span className="dg-priLabel">{t('assistant:tuningGuide.priority.stop')}</span>
                <input
                    className="tcf-input dg-priPct" type="number" min={50} max={99} value={value.stopAt} readOnly={readOnly}
                    aria-label={t('assistant:tuningGuide.priority.stop')}
                    onChange={(e) => onChange({ ...value, stopAt: e.target.value })}
                />
                <span className="tcf-hint">{t('assistant:tuningGuide.priority.stopHint')}</span>
            </div>
            {problems.badStop ? <div className="gm-dropped">{t('assistant:tuningGuide.priority.stopRange')}</div> : null}
        </div>
    );
}

TuningPriorityEditor.propTypes = {
    value: PropTypes.shape({ fixOrder: PropTypes.arrayOf(PropTypes.string), values: PropTypes.string, stopAt: PropTypes.string }).isRequired,
    onChange: PropTypes.func.isRequired,
    fixTopics: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string, label: PropTypes.string })).isRequired,
    readOnly: PropTypes.bool,
    limits: PropTypes.shape({ values: PropTypes.number, valueChars: PropTypes.number }),
    t: PropTypes.func.isRequired,
};
TuningPriorityEditor.defaultProps = { readOnly: false, limits: { values: 8, valueChars: 120 } };

export default TuningPriorityEditor;
